<!--
  AvailabilityBadge — the member-facing «مش متوفر» strip.

  ONE dated fact, stated plainly: when the club last failed to source this, and
  at which supplier. Tap it and it tells the rest — which variants (or that it is
  the whole product), and the admin's public note if they wrote one.

  WHAT IT MUST NOT DO, and why:
   - It promises NOTHING about a return. There is no back-in-stock notification
     and there never will be in v1, so a bell / follow / "we'll tell you" here
     would be a promise the system cannot keep. `since` is a freshness signal,
     not a countdown — the old `{state, until}` shape that implied one is gone.
   - It does not BLOCK anything. Ordering during a mark stays allowed on purpose
     (scope lock V4); this informs, and the buying controls beside it stay live.
   - It reads WEAKER than an item's own «نواقص» status. That one is a verdict
     about her order; this is an observation about a product. Muted tone
     (--ci-muted-*, documented as exactly the out-of-stock terminal-awareness
     pair), never the danger pair: nothing is wrong and nothing is broken.

  Contract + guards live in utils/productAvailability.js. This component renders
  an already-normalised hint and never touches the raw attribute.
-->
<template>
  <div class="availability-badge" :class="{ 'availability-badge--open': expanded }">
    <button
      type="button"
      class="availability-badge__summary"
      :aria-expanded="expanded ? 'true' : 'false'"
      :aria-controls="detailId"
      @click.stop="toggle"
    >
      <f7-icon f7="exclamationmark_circle" size="16" class="availability-badge__icon"></f7-icon>
      <span class="availability-badge__text">{{ summary }}</span>
      <span v-if="hasDetail" class="availability-badge__hint">
        {{ t('product.availability.expandHint') }}
        <f7-icon f7="chevron_down" size="12" class="availability-badge__chevron"></f7-icon>
      </span>
    </button>

    <div v-if="hasDetail && expanded" :id="detailId" class="availability-badge__detail">
      <div class="availability-badge__row">
        <span class="availability-badge__label">
          {{ t('product.availability.variantsLabel') }}
        </span>
        <!-- «المنتج كله» replaces the list rather than joining it: a
             whole-product claim is not one more affected variant, it is the
             statement that naming variants is beside the point. -->
        <span v-if="hint.wholeProduct" class="availability-badge__chip">
          {{ t('product.availability.wholeProduct') }}
        </span>
        <span v-for="name in hint.variants" v-else :key="name" class="availability-badge__chip">{{
          name
        }}</span>
      </div>

      <div v-if="hint.note" class="availability-badge__row availability-badge__row--note">
        <span class="availability-badge__label">
          {{ t('product.availability.noteLabel') }}
        </span>
        <span class="availability-badge__note">{{ hint.note }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatAvailabilityDate } from '@/utils/productAvailability.js';

const props = defineProps({
  /** A normalised hint from `availabilityHint()` — never a raw attribute. */
  hint: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n({ useScope: 'global' });

const expanded = ref(false);
const detailId = `availability-detail-${useId()}`;

const dateLabel = computed(() => formatAvailabilityDate(props.hint.since, locale.value));

/**
 * Four shapes, because two independent facts are optional-by-omission:
 * `supplier` is absent for the ~0.8% of marks whose source item resolved to no
 * supplier, and `wholeProduct` changes the CLAIM («مش متوفر خالص» — not
 * available at all — rather than "these ones are not"). Interpolating an empty
 * supplier into one generic string would leave a dangling «عند» in front of
 * nothing, which is why these are four keys and not one with a conditional.
 */
const summary = computed(() => {
  const group = props.hint.wholeProduct
    ? 'product.availability.badgeWhole'
    : 'product.availability.badge';

  return props.hint.supplier
    ? t(`${group}.withSupplier`, { date: dateLabel.value, supplier: props.hint.supplier })
    : t(`${group}.noSupplier`, { date: dateLabel.value });
});

/**
 * Only offer the tap when there is something behind it. A per-variant mark
 * always has a list worth showing; a whole-product mark has already said
 * everything in the summary unless the admin left a note. An expander that
 * opens onto nothing is worse than no expander.
 */
const hasDetail = computed(
  () => Boolean(props.hint.note) || (!props.hint.wholeProduct && props.hint.variants.length > 0),
);

function toggle() {
  if (!hasDetail.value) return;
  expanded.value = !expanded.value;
}
</script>

<style scoped>
.availability-badge {
  /* The out-of-stock terminal-awareness pair. NOT --ci-danger-*: nothing is
     wrong, nothing is blocked, and a red strip beside a live buy button reads
     as a refusal. */
  background: var(--ci-muted-bg);
  color: var(--ci-muted-fg);
  border-radius: var(--poolit-radius-md, 12px);
  margin: 8px 12px 0;
  overflow: hidden;
}

.availability-badge__summary {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: 0;
  color: inherit;
  font: inherit;
  font-size: 13px;
  line-height: 1.4;
  text-align: start;
  cursor: pointer;
}

.availability-badge__icon {
  flex-shrink: 0;
}

.availability-badge__text {
  flex: 1;
  min-width: 0;
}

.availability-badge__hint {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  font-size: 12px;
  opacity: 0.75;
}

.availability-badge__chevron {
  transition: transform var(--poolit-transition-fast, 150ms) var(--poolit-transition-ease, ease);
}

.availability-badge--open .availability-badge__chevron {
  transform: rotate(180deg);
}

.availability-badge__detail {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 10px 10px;
  font-size: 12px;
}

.availability-badge__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

/* The note STACKS, unlike the variants row. That row gets away with an inline
   label because the chips beside it are their own visual break; plain prose has
   none, so an inline label runs straight into the admin's sentence and the two
   read as one. Caught on device, 2026-08-16. */
.availability-badge__row--note {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.availability-badge__label {
  opacity: 0.75;
}

.availability-badge__chip {
  padding: 2px 8px;
  border-radius: 999px;
  background: color-mix(in srgb, currentColor 12%, transparent);
  font-weight: 500;
}

.availability-badge__note {
  flex: 1;
  min-width: 0;
  line-height: 1.5;
  /* The admin's own words, so they wrap rather than truncate. */
  overflow-wrap: anywhere;
}

@media (prefers-reduced-motion: reduce) {
  .availability-badge__chevron {
    transition: none;
  }
}
</style>
